<template>
    <vx-modal v-model:visible="visible" size="sm" padding="standard">
        <template v-slot:header>Delete brand</template>
        <template v-slot:subheader>{{ props.brandId }}</template>

        <div class="text-sm text-slate-800">
            This cannot be undone; the brand leaves the list once the sync ages it out, about a day.
        </div>

        <template v-slot:footer>
            <div class="flex flex-col-reverse gap-4 lg:flex-row">
                <vx-button
                    @click="visible = false"
                    type="button"
                    class="grow lg:w-20 lg:grow-0"
                    size="lg"
                    color="muted"
                >
                    Cancel
                </vx-button>
                <vx-button
                    @click="confirm()"
                    data-test="confirm-delete"
                    :loading="deleting"
                    type="button"
                    class="grow"
                    size="lg"
                    color="danger"
                >
                    Delete brand
                </vx-button>
            </div>
        </template>
    </vx-modal>
</template>

<script lang="ts" setup>
import { VxButton, VxModal } from '@voxie/frontend-components';
import { ref } from 'vue';
import { useCallout } from '~/composables/useCallout';
import { useToasted } from '~/composables/useToasted';
import { adminTenDlcRegistrationClient } from '~/services';
import dayjs from '~/utils/dayjs';
import { escapeHtml } from '~/utils/html';
import { catchRequestError } from '~/utils/request';

const props = defineProps<{ brandId: string; displayName: string | null }>();
const visible = defineModel<boolean>('visible', { default: false });

const toasted = useToasted();
const callout = useCallout();

const deleting = ref(false);

const confirm = async () => {
    if (deleting.value) {
        return;
    }

    deleting.value = true;

    try {
        await adminTenDlcRegistrationClient.deleteBrand(props.brandId);

        const name = escapeHtml(props.displayName ?? props.brandId);
        const id = escapeHtml(props.brandId);

        callout.success(
            'kiosk-tcr-brands',
            `<strong>${name}</strong> (${id}) deleted; it leaves this list once the sync ages it out, about a day`,
            {
                ttl: dayjs().add(30, 'seconds'),
                shadow: true,
            }
        );

        window.location.assign('/kiosk/ten-dlc/brands');
    } catch (e) {
        toasted.error(catchRequestError(e), { sticky: true });
    } finally {
        deleting.value = false;
    }
};
</script>
